import React, { FormEvent, useEffect, useState } from "react";
import "./CampaignRegistration.scss";
import httpClient from "../../services/httpClient";
import ROUTES from "../../routes";
import store, { Message } from "../../services/store";
import { t } from "../../services/i18n";
import LoginInput from "../Login/LoginInput";

interface CampaignInfo {
  campaign: {
    id: string;
    title: string;
    description?: string;
    license_name: string;
  };
}

interface Props {
  token: string;
}

const CampaignRegistration: React.FC<Props> = ({ token }) => {
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [passwordConfirmation, setPasswordConfirmation] = useState("");
  const [campaignInfo, setCampaignInfo] = useState<CampaignInfo | null>(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [statusMessage, setStatusMessage] = useState<{
    type: "success" | "info" | "error";
    text: string;
  } | null>(null);

  useEffect(() => {
    const fetchCampaignInfo = async () => {
      setLoading(true);
      try {
        const res = await httpClient.req(
          ROUTES.GET_CAMPAIGN_REGISTRATION_INFO({ token })
        );
        setCampaignInfo(res);
      } catch (error) {
        console.warn(error);
        store.notify(
          Message.Error,
          t("Token d'inscription invalide ou expiré")
        );
        setCampaignInfo(null);
      } finally {
        setLoading(false);
      }
    };

    if (token) {
      fetchCampaignInfo();
    }
  }, [token]);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();

    if (password !== passwordConfirmation) {
      store.notify(Message.Error, t("Les mots de passe ne correspondent pas"));
      return;
    }

    if (password.length < 6) {
      store.notify(
        Message.Error,
        t("Le mot de passe doit contenir au moins 6 caractères")
      );
      return;
    }

    setSubmitting(true);

    try {
      const response = await httpClient.req(
        ROUTES.REGISTER_TO_CAMPAIGN({
          token,
          user: {
            first_name: firstName,
            last_name: lastName,
            email,
            password,
            password_confirmation: passwordConfirmation,
          },
        })
      );

      const message =
        response?.message ||
        t(
          "Inscription réussie ! Vous pouvez maintenant vous connecter avec vos identifiants."
        );
      store.notify(Message.Notification, message);

      const normalized = message.toLowerCase();
      const existingUser =
        normalized.includes("already registered") ||
        normalized.includes("already exists");

      const text = existingUser
        ? t(
            "Ce compte existe déjà. Veuillez vous connecter pour accéder à la campagne."
          )
        : message;

      setStatusMessage({
        type: existingUser ? "info" : "success",
        text,
      });

      // Laisser le temps de lire le message avant de rediriger vers le login unifié
      setTimeout(() => {
        window.location.replace("/login");
      }, 2000);
    } catch (error: any) {
      console.warn(error);
      const errorMessage =
        error?.response?.data?.errors?.join?.(", ") ||
        error?.response?.data?.error ||
        t("Impossible de finaliser l'inscription");
      store.notify(Message.Error, errorMessage);
      setStatusMessage({ type: "error", text: errorMessage });
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="CampaignRegistration CampaignRegistration--boxed">
        <div className="CampaignRegistration__loading">
          {t("Chargement des informations de campagne...")}
        </div>
      </div>
    );
  }

  if (!campaignInfo) {
    return (
      <div className="CampaignRegistration CampaignRegistration--boxed">
        <div className="CampaignRegistration__error">
          <h2>{t("Token invalide")}</h2>
          <p>{t("Le lien d'inscription est invalide ou a expiré.")}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="CampaignRegistration CampaignRegistration--boxed">
      <div className="CampaignRegistration__header">
        <h3>{t("Inscription à la campagne")}</h3>
        <p>{campaignInfo.campaign.title}</p>
        {campaignInfo.campaign.description ? (
          <small>{campaignInfo.campaign.description}</small>
        ) : null}
      </div>
      {statusMessage && (
        <div
          className={`CampaignRegistration__alert CampaignRegistration__alert--${statusMessage.type}`}
        >
          {statusMessage.text}
        </div>
      )}
      <form className="CampaignRegistration__form" onSubmit={handleSubmit}>
        <LoginInput
          type="text"
          value={firstName}
          onChange={setFirstName}
          placeholder={t("Prénom")}
          required
        />
        <LoginInput
          type="text"
          value={lastName}
          onChange={setLastName}
          placeholder={t("Nom")}
          required
        />
        <LoginInput
          type="email"
          value={email}
          onChange={setEmail}
          placeholder={t("Email professionnel")}
          required
        />
        <LoginInput
          type="password"
          value={password}
          onChange={setPassword}
          placeholder={t("Mot de passe")}
          required
        />
        <LoginInput
          type="password"
          value={passwordConfirmation}
          onChange={setPasswordConfirmation}
          placeholder={t("Confirmer le mot de passe")}
          required
        />

        <button type="submit" disabled={submitting}>
          {submitting ? t("Inscription en cours...") : t("S'inscrire")}
        </button>
      </form>
    </div>
  );
};

export default CampaignRegistration;
